import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getUserLibrary, type LibraryPaginatedResponse, type GetUserLibraryParams } from '../api/library';
import type { LibraryStatus, LibraryItem } from '../api/library';
import { useMemo } from 'react';

interface UseUserLibraryOptions {
  page?: number;
  limit?: number;
}

export const useUserLibrary = (options: UseUserLibraryOptions = {}) => {
  const { page = 1, limit = 10 } = options;
  
  return useQuery<LibraryPaginatedResponse, Error>({
    queryKey: ['userLibrary', page, limit],
    queryFn: () => getUserLibrary({ page, limit }),
    staleTime: 60_000,
  });
};

export type LibraryTab = 'in-progress' | 'saved' | 'completed';

interface UseUserLibraryInfiniteOptions {
  limit?: number;
}

export const useUserLibraryInfinite = (tab: LibraryTab, options: UseUserLibraryInfiniteOptions = {}) => {
  const { limit = 10 } = options;

  const query = useInfiniteQuery({
    queryKey: ['userLibraryInfinite', tab, limit],
    queryFn: async ({ pageParam = 1 }) => {
      const result = await getUserLibrary({ page: pageParam, limit });
      return result;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination?.has_next) {
        return (lastPage.pagination.page || 1) + 1;
      }
      return undefined;
    },
    staleTime: 30_000,
  });

  // Flatten and filter items based on tab
  const items = useMemo(() => {
    if (!query.data?.pages) return [];
    
    const allItems = query.data.pages.flatMap((page) => page.items || []);
    
    if (tab === 'in-progress') {
      return allItems.filter((i: LibraryItem) => i?.status === 'in-progress');
    } else if (tab === 'saved') {
      return allItems.filter((i: LibraryItem) => i?.is_saved === true);
    } else if (tab === 'completed') {
      return allItems.filter((i: LibraryItem) => i?.status === 'completed');
    }
    return allItems;
  }, [query.data?.pages, tab]);

  return {
    items,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isError: query.isError,
  };
};


// Simple hook for dashboard (first page only)
interface UseUserLibraryForTabOptions {
  page?: number;
  limit?: number;
}

export const useUserLibraryForTab = (tab: LibraryTab, options: UseUserLibraryForTabOptions = {}) => {
  const { page = 1, limit = 10 } = options;
  
  const query = useQuery<LibraryPaginatedResponse, Error>({
    queryKey: ['userLibrary', tab, page, limit],
    queryFn: () => getUserLibrary({ page, limit }),
    staleTime: 30_000,
  });

  const data = useMemo<LibraryPaginatedResponse | undefined>(() => {
    const src = (query.data as any)?.items || [];
    let items = src;
    if (tab === 'in-progress') {
      items = src.filter((i: any) => i?.status === 'in-progress');
    } else if (tab === 'saved') {
      items = src.filter((i: any) => i?.is_saved === true);
    } else if (tab === 'completed') {
      items = src.filter((i: any) => i?.status === 'completed');
    }
    return query.data ? { 
      items, 
      count: items.length,
      pagination: query.data.pagination 
    } : undefined;
  }, [tab, query.data]);

  return { data, isLoading: query.isLoading, isError: query.isError } as const;
};
